
/**
 * v0.7 中英切換。
 *
 * - 中文是原文；英文放在 content/i18n/en.json（key＝中文原句，value＝英文）。
 * - 程式裡的中文都包成 tr('中文 {0}', 值)：中文版直接用原句，英文版查字典，查不到就顯示中文。
 * - 內容 JSON（任務、案件、地圖……）載入時整份換成英文（translateDeep）。
 * - 切換語言會重新整理畫面（存檔不受影響）。
 * - 新增中文內容後，跑 npm run i18n:check 看還有哪些句子沒有英文。
 */
export type Lang = 'zh' | 'en';
const KEY = 'ffg.lang';
let DICT: Record<string, string> = {};

/** 英文版才下載英文字典（中文版不用多載 400 KB） */
export async function loadLang(): Promise<void> {
  if (lang === 'en') DICT = (await import('../../content/i18n/en.json')).default as Record<string, string>;
}

function detect(): Lang {
  try {
    const q = new URLSearchParams(globalThis.location?.search ?? '').get('lang');
    if (q === 'en' || q === 'zh') return q;
    const s = globalThis.localStorage?.getItem(KEY);
    if (s === 'en' || s === 'zh') return s;
  } catch { /* 無痕模式等 */ }
  return 'zh';
}
export let lang: Lang = detect();
export const isEn = () => lang === 'en';

/** 測試用：直接切換，不重新整理 */
export function _setLangForTest(l: Lang, dict?: Record<string, string>) { lang = l; if (dict) DICT = dict; }

export function setLang(l: Lang): void {
  try { globalThis.localStorage?.setItem(KEY, l); } catch { /* ignore */ }
  try { const u = new URL(location.href); u.searchParams.delete('lang'); location.replace(u.toString()); } catch { /* ignore */ }
}

const fill = (s: string, args: unknown[]) => args.length ? s.replace(/\{(\d+)\}/g, (m, i) => (Number(i) < args.length ? String(args[Number(i)]) : m)) : s;

/** 翻譯一句（{0}、{1}… 會換成後面的值） */
export function tr(zh: string, ...args: unknown[]): string {
  return fill(lang === 'en' ? DICT[zh] ?? zh : zh, args);
}

const CJK = /[㐀-鿿！-～　-〿・]/;
/** 英文版：把整份內容裡的中文字串換成英文（只換值，不換 key／id） */
export function translateDeep<T>(v: T): T {
  if (lang !== 'en') return v;
  const walk = (x: unknown): unknown => {
    if (typeof x === 'string') return CJK.test(x) ? DICT[x] ?? x : x;
    if (Array.isArray(x)) return x.map(walk);
    if (x && typeof x === 'object') { const o: Record<string, unknown> = {}; for (const [k, val] of Object.entries(x)) o[k] = walk(val); return o; }
    return x;
  };
  return walk(v) as T;
}

/** 英文版：把 index.html 裡寫死的中文（按鈕、提示文字、標題）換成英文 */
export function translateStaticDom(root: ParentNode = document): void {
  if (lang !== 'en') return;
  document.documentElement.lang = 'en';
  document.title = DICT[document.title] ?? document.title;
  const w = document.createTreeWalker(root as Node, NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) {
    const t = n.nodeValue ?? ''; const k = t.trim();
    if (k && CJK.test(k) && DICT[k]) n.nodeValue = t.replace(k, DICT[k]);
  }
  for (const el of (root as ParentNode).querySelectorAll('[title],[aria-label],meta[content]')) {
    for (const a of ['title', 'aria-label', 'content']) { const v = el.getAttribute(a); if (v && CJK.test(v) && DICT[v]) el.setAttribute(a, DICT[v]); }
  }
}
